import { useState } from 'react';
import { AxiosError } from 'axios';
import { message } from 'antd';
import { useCouponListQuery } from '../queries/coupon/useCouponListQuery';
import { CouponApiV2 } from '../../lib/api/coupon/CouponApiV2';
import { excelDownLoad } from '../../lib/common';
import { CouponListData, CouponListFilterOption, CouponListOption, CouponListSearchOption } from '../../types/coupons/list-v2';
import { NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT } from '../../constants/service/common/utilConstants';
import { Sort } from '../../types/common/options';

const INITIAL_FILTER_OPTION: CouponListFilterOption = {
  couponId: null,
  name: null,
  description: null,
  status: null,
  isDownloadable: 0,
  availableType: null,
  discountType: null,
  code: null,
  createDate: {
    startDate: null,
    endDate: null,
  },
};

const INITIAL_SEARCH_OPTION: CouponListSearchOption = {
  couponAvailability: false,
};

const INITIAL_SORT_OPTION: Sort<'id' | 'status' | 'name' | 'createDate'> = '-createDate';

const INITIAL_PAGE_SIZE = 20;

type UseCouponListReturnType = {
  couponList: CouponListData['list'];
  totalCount: number;
  isLoading: boolean;
  error: AxiosError | null;
  filterOption: CouponListFilterOption;
  searchOption: CouponListSearchOption;
  searchData: CouponListSearchOption;
  sortOption: Sort<'id' | 'status' | 'name' | 'createDate'>;
  pagination: { pageNum: number; pageSize: number; pages: number };
  onFilter: (filter: Partial<CouponListFilterOption>) => void;
  onSort: (sort: Sort<'id' | 'status' | 'name' | 'createDate'>) => void;
  onPageChange: (page: number, pageSize: number) => void;
  onSearch: (searchOption: CouponListSearchOption) => void;
  onClear: () => void;
  onSearchDataChange: (data: Partial<CouponListSearchOption>) => void;
  onExcelDownload: () => void;
};

export const useCouponList = (): UseCouponListReturnType => {
  const [option, setOption] = useState<CouponListOption>({
    page: 0,
    pageSize: INITIAL_PAGE_SIZE,
    filterOption: INITIAL_FILTER_OPTION,
    searchOption: INITIAL_SEARCH_OPTION,
    sortOption: INITIAL_SORT_OPTION,
  });

  const [searchData, setSearchData] = useState<CouponListSearchOption>(INITIAL_SEARCH_OPTION);

  const { data, isLoading, error } = useCouponListQuery(option);

  const onFilter = (filter: Partial<CouponListFilterOption>) => {
    setOption((prev) => ({
      ...prev,
      page: 0,
      filterOption: { ...prev.filterOption!, ...filter },
    }));
  };

  const onSort = (sort: Sort<'id' | 'status' | 'name' | 'createDate'>) => {
    setOption((prev) => ({
      ...prev,
      sortOption: sort,
    }));
  };

  const onPageChange = (page: number, pageSize: number) => {
    setOption((prev) => ({
      ...prev,
      page,
      pageSize,
    }));
  };

  const onSearch = (searchOption: CouponListSearchOption) => {
    setOption((prev) => ({
      ...prev,
      page: 0,
      searchOption,
    }));
  };

  const onClear = () => {
    setOption({
      page: 0,
      pageSize: INITIAL_PAGE_SIZE,
      filterOption: INITIAL_FILTER_OPTION,
      searchOption: INITIAL_SEARCH_OPTION,
      sortOption: INITIAL_SORT_OPTION,
    });
    setSearchData(INITIAL_SEARCH_OPTION);
  };

  const onSearchDataChange = (partialData: Partial<CouponListSearchOption>) => {
    setSearchData((prev) => ({ ...prev, ...partialData }));
  };

  const onExcelDownload = async () => {
    if (!data?.list || !data.list.length) {
      message.error(NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT);
      return;
    }

    try {
      const result = await CouponApiV2.getCouponListExcelDownload({
        searchOption: option.searchOption,
        filterOption: option.filterOption,
        sortOption: option.sortOption ? [{ id: option.sortOption.startsWith('-') ? option.sortOption.slice(1) : option.sortOption, desc: option.sortOption.startsWith('-') }] : [],
      });
      excelDownLoad('쿠폰 리스트', result.data.couponListExcelSheet);
    } catch (e) {
      message.error('엑셀 다운로드에 실패했습니다.');
    }
  };

  const totalCount = data?.totalCount ?? 0;

  return {
    couponList: data?.list ?? [],
    totalCount,
    isLoading,
    error,
    filterOption: option.filterOption!,
    searchOption: option.searchOption!,
    searchData,
    sortOption: option.sortOption!,
    pagination: {
      pageNum: option.page,
      pageSize: option.pageSize,
      pages: Math.ceil(totalCount / option.pageSize) || 1,
    },
    onFilter,
    onSort,
    onPageChange,
    onSearch,
    onClear,
    onSearchDataChange,
    onExcelDownload,
  };
};
